import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Quote, Star } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
interface Testimonial {
  id: string;
  name: string;
  role: string;
  university: string | null;
  content: string;
  rating: number;
  image_url: string | null;
}
const Testimonials = () => {
  const isMobile = useIsMobile();
  const [testimonials, setTestimonials] = useState<Testimonial[]>([]);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [showAll, setShowAll] = useState(false);
  
  useEffect(() => {
    const fetchTestimonials = async () => {
      const {
        data,
        error
      } = await supabase.from("testimonials").select("*").eq("featured", true).order("created_at", {
        ascending: false
      });
      if (error) {
        // Error fetching testimonials - handle gracefully
      } else if (data) {
        setTestimonials(data);
      }
    };
    fetchTestimonials();
  }, []);
  
  const displayedTestimonials = isMobile && !showAll ? testimonials.slice(0, 2) : testimonials;
  
  return <section className="py-16 md:py-20 bg-secondary/30">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12 md:mb-16 animate-fade-in">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-3 md:mb-4">
            Hear From Our <span className="bg-gradient-to-r from-primary to-primary-glow bg-clip-text text-slate-700">Students</span>
          </h2>
          <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto">
            <span className="hidden md:inline">Real experiences from students who trusted us with their journey</span>
            <span className="md:hidden">Real student experiences</span>
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6 md:gap-8 max-w-6xl mx-auto">
          {displayedTestimonials.map((testimonial, index) => <Card key={testimonial.id} className="relative overflow-hidden hover:shadow-elegant transition-all duration-300 hover:-translate-y-2 animate-scale-in" style={{
          animationDelay: `${index * 100}ms`
        }}>
              <CardContent className="p-4 md:p-6">
                <Quote className="h-6 w-6 md:h-8 md:w-8 text-primary/20 mb-3 md:mb-4" />
                
                <div className="flex gap-1 mb-4">
                  {Array.from({
                length: testimonial.rating
              }).map((_, i) => <Star key={i} className="h-4 w-4 fill-primary text-primary" />)}
                </div>

                <div className="mb-4 md:mb-6">
                  <p className={`text-sm md:text-base text-muted-foreground ${expandedIds.has(testimonial.id) ? '' : 'line-clamp-3 md:line-clamp-4'}`}>
                    "{testimonial.content}"
                  </p>
                  {testimonial.content.length > 100 && (
                    <button
                      onClick={() => {
                        const newExpanded = new Set(expandedIds);
                        if (expandedIds.has(testimonial.id)) {
                          newExpanded.delete(testimonial.id);
                        } else {
                          newExpanded.add(testimonial.id);
                        }
                        setExpandedIds(newExpanded);
                      }}
                      className="text-primary hover:underline text-xs md:text-sm mt-2"
                    >
                      {expandedIds.has(testimonial.id) ? 'Show less' : 'More...'}
                    </button>
                  )}
                </div>

                <div className="flex items-center gap-2 md:gap-3">
                  <Avatar className="h-8 w-8 md:h-10 md:w-10">
                    <AvatarImage src={testimonial.image_url || undefined} />
                    <AvatarFallback className="bg-primary/10 text-primary text-xs md:text-sm">
                      {testimonial.name.split(" ").map(n => n[0]).join("")}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="text-sm md:text-base font-semibold">{testimonial.name}</p>
                    <p className="text-xs md:text-sm text-muted-foreground">{testimonial.role}</p>
                    {testimonial.university && <p className="text-xs text-muted-foreground">{testimonial.university}</p>}
                  </div>
                </div>
              </CardContent>
            </Card>)}
        </div>

        {isMobile && testimonials.length > 2 && (
          <div className="flex justify-center mt-8">
            <button
              onClick={() => setShowAll(!showAll)}
              className="px-6 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
            >
              {showAll ? "Show Less" : "Show More"}
            </button>
          </div>
        )}
      </div>
    </section>;
};
export default Testimonials;